import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { safeRest, defaultBaseline, defaultFonts, uniqueId } from '@wds/utils';
import { Label } from '@wds/label';
import { IconCaretDown } from '@wds/icons';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var Option = function Option(_a) {
  var children = _a.children,
    _b = _a.disabled,
    disabled = _b === void 0 ? false : _b,
    value = _a.value,
    rest = __rest(_a, ["children", "disabled", "value"]);
  return jsx("option", __assign({
    disabled: disabled,
    value: value
  }, safeRest(rest), {
    children: children
  }));
};

// Optgroup elements may not be nested. Please see the spec https://w3c.github.io/html-reference/optgroup.html#optgroup
var OptionGroups = function OptionGroups(_a) {
  var children = _a.children,
    label = _a.label,
    disabled = _a.disabled,
    rest = __rest(_a, ["children", "label", "disabled"]);
  return jsx("optgroup", __assign({
    label: label,
    disabled: disabled
  }, safeRest(rest), {
    children: children
  }));
};

var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    border = theme.border,
    radius = theme.radius,
    shadow = theme.shadow,
    spacing = theme.spacing,
    type = theme.type;
  var select = {
    height: {
      sm: 32,
      md: 40,
      lg: 48
    },
    borderRadius: radius.small,
    iconColor: palette.muted.contrast
  };
  var selectStyles = {
    wrenchSelectContainer: __assign(__assign({
      fontFamily: type.fontFamily.base,
      '-webkit-font-smoothing': 'antialiased'
    }, defaultBaseline), {
      position: 'relative',
      display: 'inline-block',
      cursor: 'pointer',
      backgroundColor: palette.background.light,
      border: function (_a) {
        var noBorder = _a.noBorder;
        return noBorder ? 'none' : [border.main];
      },
      borderColor: function (_a) {
        var color = _a.color;
        return color ? palette[color].main : null;
      },
      fontSize: function (_a) {
        var size = _a.size;
        return size === 'sm' ? type.fontSize.bodySm : type.fontSize.body;
      },
      color: palette.text.dark,
      borderRadius: select.borderRadius,
      height: function (_a) {
        var size = _a.size;
        return size ? select.height[size] : null;
      },
      pointerEvents: function (_a) {
        var disabled = _a.disabled;
        return disabled ? 'none' : null;
      },
      opacity: function (_a) {
        var disabled = _a.disabled;
        return disabled ? '0.4' : null;
      },
      width: function (_a) {
        var stretched = _a.stretched;
        return stretched ? '100%' : null;
      },
      '&:hover': {
        borderColor: function (_a) {
          var color = _a.color;
          return color ? palette[color].main : palette.border.hover;
        },
        boxShadow: shadow.sky,
        transition: ['all', '100ms']
      },
      '&:focus, &:focus-within': {
        borderColor: function (_a) {
          var color = _a.color;
          return color ? palette[color].main : palette.border.active;
        },
        transition: ['border-color', '100ms'],
        outline: '0'
      }
    }),
    wrenchSelect: __assign(__assign({}, defaultFonts(theme)), {
      appearance: 'none',
      outline: '0',
      boxShadow: 'none',
      border: 'none',
      fontSize: function (_a) {
        var size = _a.size;
        return size === 'sm' ? type.fontSize.bodySm : type.fontSize.body;
      },
      background: 'none',
      width: '100%',
      height: '100%',
      cursor: 'pointer',
      padding: [0, spacing[6] + spacing[2], 0, spacing[4]],
      paddingLeft: function (_a) {
        var addOn = _a.addOn;
        return addOn ? spacing[6] + spacing[2] : null;
      }
    }),
    wrenchSelectAffordance: function () {
      return {
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        top: '50%',
        right: spacing[4],
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        width: '1em',
        height: '1em',
        '& > svg': {
          fill: select.iconColor
        }
      };
    },
    wrenchSelectAddOn: function () {
      return {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-0.75em)',
        left: '16px',
        paddingRight: spacing[3]
      };
    }
  };
  return selectStyles;
});

var Select = function Select(_a) {
  var children = _a.children,
    id = _a.id,
    label = _a.label,
    ariaLabel = _a.ariaLabel,
    _b = _a.disabled,
    disabled = _b === void 0 ? false : _b,
    color = _a.color,
    _c = _a.size,
    size = _c === void 0 ? 'md' : _c,
    _d = _a.multiple,
    multiple = _d === void 0 ? false : _d,
    _e = _a.stretched,
    stretched = _e === void 0 ? false : _e,
    _f = _a.noBorder,
    noBorder = _f === void 0 ? false : _f,
    addOn = _a.addOn,
    rest = __rest(_a, ["children", "id", "label", "ariaLabel", "disabled", "color", "size", "multiple", "stretched", "noBorder", "addOn"]);
  var inputID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('input');
  }, [id]);
  var _g = useStyles({
      size: size,
      color: color,
      disabled: disabled,
      multiple: multiple,
      stretched: stretched,
      addOn: addOn,
      noBorder: noBorder
    }),
    wrenchSelectContainer = _g.wrenchSelectContainer,
    wrenchSelectAddOn = _g.wrenchSelectAddOn,
    wrenchSelect = _g.wrenchSelect,
    wrenchSelectAffordance = _g.wrenchSelectAffordance;
  return jsxs(Fragment, {
    children: [!!label && jsx(Label, {
      htmlFor: inputID,
      children: label
    }), jsxs("div", {
      className: wrenchSelectContainer,
      role: "button",
      children: [addOn && jsx("div", {
        className: wrenchSelectAddOn,
        children: addOn
      }), jsx("select", __assign({
        className: wrenchSelect,
        "aria-label": ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : label,
        id: inputID,
        disabled: disabled,
        multiple: multiple
      }, safeRest(rest), {
        children: children
      })), !multiple && jsx("span", {
        className: wrenchSelectAffordance,
        children: jsx(IconCaretDown, {
          title: "Select dropdown arrow"
        })
      })]
    })]
  });
};
Select.Option = Option;
Select.OptGroup = OptionGroups;

export { Select };
